import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import ModalHelp from '../../../components/ModalHelp';
import UsuarioViewModel from '../../../viewmodel/usuario/UsuarioViewModel';
import Usuario from '../../../model/Usuario/Usuario';
import './AlterarResponsavelModal.css';

interface AlterarResponsavelModalProps {
    show: boolean;
    conta: {
        id: string;
        descricao: string;
        responsavelAtual?: string;
        isReativacao?: boolean;
    } | null;
    onConfirm: (novoResponsavelId: string | null) => Promise<void>;
    onClose: () => void;
}

const AlterarResponsavelModal: React.FC<AlterarResponsavelModalProps> = ({ 
    show, 
    conta, 
    onConfirm, 
    onClose 
}) => {
    const [usuarioEncontrado, setUsuarioEncontrado] = useState<Usuario | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [matricula, setMatricula] = useState('');

    const limparEstado = () => {
        setUsuarioEncontrado(null);
        setError(null);
        setMatricula('');
    };

    useEffect(() => {
        if (!show) {
            limparEstado();
        }
    }, [show]);

    const buscarUsuarioPorMatricula = async () => {
        if (!matricula) {
            setError('Digite uma matrícula');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const usuario = await UsuarioViewModel.buscarUsuarioPorMatricula(matricula.trim());
            setUsuarioEncontrado(usuario);
        } catch (err) {
            setUsuarioEncontrado(null);
            setError(err instanceof Error ? err.message : 'Erro ao buscar usuário');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmar = async () => {
        try {
            setLoading(true);
            await onConfirm(usuarioEncontrado?.id ?? null);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao alterar responsável');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{conta?.isReativacao ? 'Reativar Conta de Serviço' : 'Alterar Responsável'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ModalHelp text="Transfira a responsabilidade da conta para outro usuário. O novo responsável deve ter vínculo ativo com a unidade. Busque pelo CPF do novo responsável." />
                {conta && (
                    <p>
                        {conta.isReativacao
                            ? <>Reativando conta: <strong>{conta.descricao}</strong>. Opcionalmente, informe um novo responsável.</>
                            : <>Alterando responsável da conta: <strong>{conta.descricao}</strong></>}
                    </p>
                )}

                {conta?.isReativacao && (
                    <Alert variant="info">
                        Se você não informar matrícula e confirmar, a conta será reativada mantendo o responsável atual.
                    </Alert>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Buscar Usuário por Matrícula</Form.Label>
                    <div className="d-flex">
                        <Form.Control
                            type="text"
                            placeholder="Digite a matrícula"
                            value={matricula}
                            onChange={(e) => setMatricula(e.target.value)}
                        />
                        <Button 
                            variant="primary" 
                            onClick={buscarUsuarioPorMatricula}
                            disabled={loading || !matricula.trim()}
                            className="ms-2"
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
                        </Button>
                    </div>
                </Form.Group>

                {loading && !error && <Spinner animation="border" size="sm" />}

                {error && <Alert variant="danger">{error}</Alert>}

                {usuarioEncontrado && (
                    <div className="usuario-encontrado mt-3 p-3 border rounded">
                        <h5>Usuário encontrado:</h5>
                        <p><strong>Nome:</strong> {usuarioEncontrado.nome}</p>
                        <p><strong>Matrícula:</strong> {usuarioEncontrado.matricula}</p>
                        <p><strong>Email:</strong> {usuarioEncontrado.email}</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmar}
                    disabled={loading}
                >
                    {conta?.isReativacao ? 'Reativar' : 'Confirmar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AlterarResponsavelModal;
